import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Avatar
} from '@mui/material';
import {
  Handshake as HandshakeIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Euro as EuroIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '../common/LoadingOverlay';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import cashSettlementService from '../../services/cashSettlementService';
import disputeAdminService from '../../services/disputeAdminService';
import { getCurrencySymbol } from '../../utils/currency';

// Helper to build profile picture URL with a stable cache-buster (matches DisputeDetailPage)
const getProfilePictureUrl = (user) => {
  if (!user?.profilePicture) return '';
  const url = String(user.profilePicture);
  const simpleHash = url.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
  }, 0);
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${Math.abs(simpleHash).toString(36)}`;
};

// Helper pour formater le montant (DECIMAL de PostgreSQL retourne une string)
const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '0.00';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

/**
 * CashSettlementCard - Composant pour afficher et gérer un règlement amiable (cash)
 * 
 * Ce composant est affiché lorsqu'un dispute avec paiement cash est en statut 'pending_settlement'
 */
const CashSettlementCard = ({ disputeId, currentUserId, onSettlementComplete, onRefresh }) => {
  const { t, i18n } = useTranslation('disputes');
  const dateLocale = i18n?.language?.startsWith('fr') ? fr : enUS;
  
  const [settlement, setSettlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [confirmPayerDialogOpen, setConfirmPayerDialogOpen] = useState(false);
  const [confirmRecipientDialogOpen, setConfirmRecipientDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  
  // Preuve de paiement - fichier optionnel à joindre lors de la confirmation
  const [proofOfPaymentFile, setProofOfPaymentFile] = useState(null);
  const proofFileInputRef = useRef(null);
  const [proofFileError, setProofFileError] = useState(null);
  
  // Fetch settlement
  const fetchSettlement = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cashSettlementService.getByDispute(disputeId);
      if (response.success) {
        setSettlement(response.data);
      } else {
        setError(response.error || 'Impossible de charger le règlement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [disputeId]);
  
  useEffect(() => {
    fetchSettlement();
  }, [fetchSettlement]);
  
  // Determine user role in settlement
  const isPayer = settlement?.payer_id === currentUserId;
  const isRecipient = settlement?.recipient_id === currentUserId;
  
  // Get settlement status info
  const getStatusInfo = (status) => {
    const statusMap = {
      pending: {
        label: t('settlement.status.pending', 'En attente du paiement'),
        color: 'warning',
        icon: <AccessTimeIcon />
      },
      payer_confirmed: {
        label: t('settlement.status.payer_confirmed', 'Paiement confirmé par le payeur'),
        color: 'info',
        icon: <CheckCircleIcon />
      },
      recipient_confirmed: {
        label: t('settlement.status.recipient_confirmed', 'Réception confirmée'),
        color: 'success',
        icon: <CheckCircleIcon />
      },
      completed: {
        label: t('settlement.status.completed', 'Règlement terminé'),
        color: 'success',
        icon: <CheckCircleIcon />
      },
      disputed: {
        label: t('settlement.status.disputed', 'Contesté'),
        color: 'error',
        icon: <ErrorOutlineIcon />
      },
      expired: {
        label: t('settlement.status.expired', 'Expiré'),
        color: 'default',
        icon: <CancelIcon />
      },
      cancelled: {
        label: t('settlement.status.cancelled', 'Annulé'),
        color: 'default',
        icon: <CancelIcon />
      }
    };
    return statusMap[status] || { label: status, color: 'default', icon: null };
  };
  
  // Get active step for stepper
  const getActiveStep = (status) => {
    const steps = ['pending', 'payer_confirmed', 'completed'];
    if (status === 'disputed' || status === 'expired' || status === 'cancelled') {
      return -1; // Error state
    }
    return steps.indexOf(status === 'recipient_confirmed' ? 'completed' : status);
  };
  
  // Handle payer confirmation
  const handleConfirmPayerPaid = async () => {
    try {
      setActionLoading(true);
      
      // ÉTAPE 1: Confirmer le paiement (sans fichier)
      const response = await cashSettlementService.confirmPayerPaid(settlement.id, null, disputeId);
      
      if (response.success) {
        setSettlement(response.data);
        setConfirmPayerDialogOpen(false);
        
        // ÉTAPE 2: Si un fichier a été sélectionné, l'envoyer comme message système
        if (proofOfPaymentFile) {
          try {
            const formData = new FormData();
            formData.append('event_type', 'system_note');
            formData.append('message', t('settlement.proof_of_payment_message', 
              'Preuve de paiement jointe par le payeur'))
            formData.append('attachments', proofOfPaymentFile);
            
            await disputeAdminService.addEvent(disputeId, formData);
          } catch (err) {
            console.error('Failed to send proof of payment attachment:', err);
            // Non-bloquant, le paiement a quand même été confirmé
          }
        }
        
        // Réinitialiser l'état du fichier
        setProofOfPaymentFile(null);
        setProofFileError(null);
        if (proofFileInputRef.current) {
          proofFileInputRef.current.value = '';
        }
        
        if (onRefresh) onRefresh();
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle recipient confirmation
  const handleConfirmRecipientReceived = async () => {
    try {
      setActionLoading(true);
      const response = await cashSettlementService.confirmRecipientReceived(settlement.id, disputeId);
      if (response.success) {
        setSettlement(response.data.settlement);
        setConfirmRecipientDialogOpen(false);
        if (onSettlementComplete) onSettlementComplete(response.data.dispute);
        if (onRefresh) onRefresh();
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle dispute settlement
  const handleDisputeSettlement = async () => {
    if (!disputeReason.trim()) {
      setError(t('validation.requiredReason'));
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await cashSettlementService.disputeSettlement(settlement.id, disputeReason, disputeId);
      if (response.success) {
        setSettlement(response.data);
        setDisputeDialogOpen(false);
        setDisputeReason('');
        if (onRefresh) onRefresh();
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle escalate to admin (when settlement deadline expired)
  const handleEscalateToAdmin = async () => {
    try {
      setActionLoading(true);
      const reason = escalateReason.trim() || 'Délai de règlement amiable expiré';
      const response = await disputeAdminService.escalate(disputeId, { reason });
      if (response.success) {
        setEscalateDialogOpen(false);
        setEscalateReason('');
        if (onRefresh) onRefresh();
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }
  
  if (!settlement) {
    return null;
  }
  
  const statusInfo = getStatusInfo(settlement.status);
  const isExpired = settlement.due_date && isPast(new Date(settlement.due_date));
  const activeStep = getActiveStep(settlement.status);
  
  return (
    <>
      <Card sx={{ mb: 2, border: '2px solid', borderColor: 'secondary.main' }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HandshakeIcon color="secondary" />
            <Typography variant="h6" component="div">
              {t('settlement.title', 'Règlement amiable')}
            </Typography>
            <Chip
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
              icon={statusInfo.icon}
            />
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Amount and type */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  💰 {t('settlement.amount', 'Montant')}
                </Typography>
                <Typography variant="h4" color="primary">                  
                  {formatAmount(settlement.amount)} {getCurrencySymbol(settlement.currency || 'EUR')}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  🏷️ {t('settlement.type', 'Type')}
                </Typography>
                <Typography variant="body1">
                  {settlement.settlement_type === 'owner_compensation' 
                    ? t('settlement.type_owner_compensation', 'Compensation propriétaire (dégâts)')
                    : t('settlement.type_renter_refund', 'Remboursement locataire')}
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          {/* Parties involved */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('settlement.payer', 'Payeur')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    src={getProfilePictureUrl(settlement.payer)}
                    sx={{ width: 32, height: 32, bgcolor: isPayer ? 'primary.main' : 'grey.400' }}
                  >
                    {!getProfilePictureUrl(settlement.payer) && <PersonIcon fontSize="small" />}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {settlement.payer?.name || 'N/A'}
                      {isPayer && <Chip label={t('settlement.you', 'Vous')} size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    {settlement.payer_confirmed_at && (
                      <Typography variant="caption" color="success.main">
                        ✓ {t('settlement.confirmed_at', 'Confirmé le')} {format(new Date(settlement.payer_confirmed_at), 'Pp', { locale: dateLocale })}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('settlement.recipient', 'Bénéficiaire')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    src={getProfilePictureUrl(settlement.recipient)}
                    sx={{ width: 32, height: 32, bgcolor: isRecipient ? 'primary.main' : 'grey.400' }}
                  >
                    {!getProfilePictureUrl(settlement.recipient) && <PersonIcon fontSize="small" />}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {settlement.recipient?.name || 'N/A'}
                      {isRecipient && <Chip label={t('settlement.you', 'Vous')} size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    {settlement.recipient_confirmed_at && (
                      <Typography variant="caption" color="success.main">
                        ✓ {t('settlement.confirmed_at', 'Confirmé le')} {format(new Date(settlement.recipient_confirmed_at), 'Pp', { locale: dateLocale })}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>
          
          {/* Progress stepper */}
          {activeStep >= 0 && (
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                <Step>
                  <StepLabel>{t('settlement.step_pending', 'En attente')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('settlement.step_payer_confirmed', 'Paiement effectué')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('settlement.step_completed', 'Terminé')}</StepLabel>
                </Step>
              </Stepper>
            </Box>
          )}
          
          {/* Due date warning - affichage en heures - différent selon le rôle */}
          {settlement.due_date && !['completed', 'cancelled', 'expired', 'payer_confirmed'].includes(settlement.status) && (
            <Alert 
              severity={isExpired ? 'error' : 'warning'} 
              sx={{ mb: 2 }}
              icon={<AccessTimeIcon />}
            >
              {isExpired 
                ? (
                    isPayer ? (
                      // Message pour le PAYEUR - avertissement sur les conséquences
                      <>
                        <AlertTitle>{t('settlement.expired_payer_title', '⚠️ Délai de paiement expiré')}</AlertTitle>
                        {t('settlement.expired_payer_warning', 
                          'Le délai de règlement est expiré. L\'autre partie peut désormais escalader le litige à un administrateur. Des pénalités peuvent être appliquées en cas de non-paiement. Effectuez le paiement au plus vite pour éviter des sanctions.'
                        )}
                      </>
                    ) : (
                      // Message pour le BÉNÉFICIAIRE - option d'escalader
                      <>
                        <AlertTitle>{t('settlement.expired_warning_title', 'Délai expiré')}</AlertTitle>
                        {t('settlement.expired_warning_detailed', 'Le délai de règlement est expiré. Vous pouvez escalader le litige à un administrateur pour qu\'il prenne une décision.')}
                      </>
                    )
                  )
                : (() => {
                    const now = new Date();
                    const dueDate = new Date(settlement.due_date);
                    const diffMs = dueDate - now;
                    const diffHours = Math.max(0, Math.round(diffMs / (1000 * 60 * 60)));
                    const diffMinutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
                    
                    if (diffHours >= 1) {
                      return t('settlement.due_date_hours', 'Délai restant: {{hours}} heure(s) (jusqu\'au {{date}})', {
                        hours: diffHours,
                        date: format(dueDate, 'dd/MM/yyyy HH:mm', { locale: dateLocale })
                      });
                    } else {
                      return t('settlement.due_date_minutes', 'Délai restant: {{minutes}} minute(s)', {
                        minutes: diffMinutes
                      });
                    }
                  })()
              }
            </Alert>
          )}
          
          {/* Bouton d'escalade si le délai est expiré - UNIQUEMENT pour le bénéficiaire */}
          {isExpired && isRecipient && !['completed', 'cancelled', 'expired', 'disputed', 'payer_confirmed'].includes(settlement.status) && (
            <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: '2px solid', borderColor: 'error.main' }}>
              
              <Button
                variant="contained"
                color="error"
                startIcon={<WarningIcon />}
                onClick={() => setEscalateDialogOpen(true)}
                disabled={actionLoading}
              >
                {t('settlement.escalate_button', 'Escalader à un administrateur')}
              </Button>
            </Box>
          )}
          
          {/* Instructions based on user role and status */}
          {settlement.status === 'pending' && isPayer && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{t('settlement.payer_instructions_title', 'Action requise')}</AlertTitle>
                {t('settlement.payer_instructions', 
                'Vous devez effectuer le paiement de {{amount}} {{currency}} à {{recipient}}. Une fois le paiement effectué, confirmez-le ci-dessous.',
                { amount: formatAmount(settlement.amount), currency: getCurrencySymbol(settlement.currency || 'EUR'), recipient: settlement.recipient?.name }
              )}
            </Alert>
          )}
          
          {settlement.status === 'pending' && isRecipient && !isExpired && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{t('settlement.recipient_waiting_title', 'En attente')}</AlertTitle>
              {t('settlement.recipient_waiting', 
                'Vous devez attendre que {{payer}} effectue le paiement de {{amount}} {{currency}}.',
                { payer: settlement.payer?.name, amount: formatAmount(settlement.amount), currency: getCurrencySymbol(settlement.currency || 'EUR') }
              )}
            </Alert>
          )}
          
          {settlement.status === 'payer_confirmed' && isRecipient && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>{t('settlement.confirm_receipt_title', 'Confirmation requise')}</AlertTitle>
              {t('settlement.confirm_receipt_instructions', 
                '{{payer}} a confirmé avoir effectué le paiement. Avez-vous bien reçu les {{amount}} {{currency}} ? Si oui, confirmez la réception. Si non, vous pouvez contester.',
                { payer: settlement.payer?.name, amount: formatAmount(settlement.amount), currency: getCurrencySymbol(settlement.currency || 'EUR') }
              )}
            </Alert>
          )}
          
          {settlement.status === 'payer_confirmed' && isPayer && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{t('settlement.waiting_recipient_title', 'En attente de confirmation')}</AlertTitle>
              {t('settlement.waiting_recipient', 
                'Vous avez confirmé avoir effectué le paiement. En attente de la confirmation de {{recipient}}.',
                { recipient: settlement.recipient?.name }
              )}
            </Alert>
          )}
          
          {settlement.status === 'completed' && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
              <AlertTitle>{t('settlement.completed_title', 'Règlement terminé')}</AlertTitle>
              {t('settlement.completed_message', 'Le règlement amiable a été effectué avec succès. Le litige est maintenant résolu.')}
            </Alert>
          )}
          
          {settlement.status === 'disputed' && (
            <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
              <AlertTitle>{t('settlement.disputed_title', 'Règlement contesté')}</AlertTitle>
              {t('settlement.disputed_message', 'Le bénéficiaire a contesté le règlement. Le litige a été escaladé à un administrateur.')}
              {settlement.dispute_reason && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('settlement.dispute_reason', 'Raison')}:</strong> {settlement.dispute_reason}
                </Typography>
              )}
            </Alert>
          )}
          
          {settlement.status === 'expired' && (
            <Alert severity="error" sx={{ mb: 2 }} icon={<AccessTimeIcon />}>
              <AlertTitle>{t('settlement.expired_title', 'Délai de règlement expiré')}</AlertTitle>
              {t('settlement.expired_message', 'Le délai de règlement amiable est expiré. Le litige a été automatiquement escaladé à un administrateur pour traitement. Vous serez notifié lorsqu\'une décision sera prise.')}
            </Alert>
          )}
        </CardContent>
        
        {/* Action buttons */}
        {!['completed', 'cancelled', 'expired', 'disputed'].includes(settlement.status) && (
          <CardActions sx={{ px: 2, pb: 2 }}>
            {/* Payer: Confirm payment button */}
            {settlement.status === 'pending' && isPayer && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={() => setConfirmPayerDialogOpen(true)}
              >
                {t('settlement.confirm_payment', 'J\'ai effectué le paiement')}
              </Button>
            )}
            
            {/* Recipient: Confirm receipt button */}
            {settlement.status === 'payer_confirmed' && isRecipient && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setConfirmRecipientDialogOpen(true)}
                >
                  {t('settlement.confirm_receipt', 'J\'ai bien reçu le paiement')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<WarningIcon />}
                  onClick={() => setDisputeDialogOpen(true)}
                >
                  {t('settlement.dispute_payment', 'Je n\'ai pas reçu le paiement')}
                </Button>
              </>
            )}
          </CardActions>
        )}
      </Card>
      
      {/* Confirm Payer Dialog */}
      <Dialog open={confirmPayerDialogOpen} onClose={() => {
        setConfirmPayerDialogOpen(false);
        setProofOfPaymentFile(null);
        setProofFileError(null);
        if (proofFileInputRef.current) {
          proofFileInputRef.current.value = '';
        }
      }}>
        <DialogTitle>{t('settlement.confirm_payment_dialog_title', 'Confirmer le paiement')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              {t('settlement.confirm_payment_dialog_text', 
                'Vous confirmez avoir effectué le paiement de {{amount}} {{currency}} à {{recipient}}. Cette action ne peut pas être annulée.',
                { amount: formatAmount(settlement?.amount), currency: getCurrencySymbol(settlement?.currency || 'EUR'), recipient: settlement?.recipient?.name }
              )}
            </Typography>
            {proofFileError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setProofFileError(null)}>
                {proofFileError}
              </Alert>
            )}
            
            {/* File upload section */}
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '2px dashed', borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                📎 {t('settlement.proof_of_payment', 'Preuve de paiement (optionnel)')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('settlement.proof_help_text', 'Joignez une capture d\'écran, un relevé bancaire, ou tout autre document prouvant le paiement')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  ref={proofFileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => {
                    if (e.target.files?.length > 0) {
                      const file = e.target.files[0];
                      // Vérifier la taille (max 10MB)
                      if (file.size > 10 * 1024 * 1024) {
                        setProofFileError(t('validation.fileTooBig', 'Le fichier ne doit pas dépasser 10 MB'));
                        e.target.value = '';
                        setProofOfPaymentFile(null);
                      } else {
                        setProofFileError(null);
                        setProofOfPaymentFile(file);
                      }
                    }
                  }}
                  style={{ display: 'none' }}
                />
                
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => proofFileInputRef.current?.click()}
                >
                  {proofOfPaymentFile ? '✓ ' : '+ '}{proofOfPaymentFile ? proofOfPaymentFile.name : t('settlement.selectFile', 'Sélectionner un fichier')}
                </Button>
                
                {proofOfPaymentFile && (
                  <>
                    <Chip
                      label={`${(proofOfPaymentFile.size / 1024).toFixed(0)} KB`}
                      size="small"
                      variant="outlined"
                    />
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() => {
                        setProofOfPaymentFile(null);
                        setProofFileError(null);
                        if (proofFileInputRef.current) {
                          proofFileInputRef.current.value = '';
                        }
                      }}
                    >
                      {t('common.clear', 'Effacer')}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmPayerDialogOpen(false);
            setProofOfPaymentFile(null);
            setProofFileError(null);
            if (proofFileInputRef.current) {
              proofFileInputRef.current.value = '';
            }
          }} disabled={actionLoading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleConfirmPayerPaid} 
            variant="contained" 
            color="primary"
            disabled={actionLoading}
          >
            {t('common.confirm', 'Confirmer')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirm Recipient Dialog */}
      <Dialog open={confirmRecipientDialogOpen} onClose={() => setConfirmRecipientDialogOpen(false)}>
        <DialogTitle>{t('settlement.confirm_receipt_dialog_title', 'Confirmer la réception')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('settlement.confirm_receipt_dialog_text', 
              'Vous confirmez avoir reçu le paiement de {{amount}} {{currency}} de la part de {{payer}}. Cette action résoudra le litige automatiquement.',
              { amount: formatAmount(settlement?.amount), currency: getCurrencySymbol(settlement?.currency || 'EUR'), payer: settlement?.payer?.name }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRecipientDialogOpen(false)} disabled={actionLoading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleConfirmRecipientReceived} 
            variant="contained" 
            color="success"
            disabled={actionLoading}
          >
            {t('common.confirm', 'Confirmer')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dispute Settlement Dialog */}
      <Dialog open={disputeDialogOpen} onClose={() => setDisputeDialogOpen(false)}>
        <DialogTitle>{t('settlement.dispute_dialog_title', 'Contester le règlement')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('settlement.dispute_dialog_text', 
              'Vous indiquez ne pas avoir reçu le paiement. Le litige sera escaladé à un administrateur pour résolution.'
            )}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={t('settlement.dispute_reason_label', 'Raison de la contestation')}
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            placeholder={t('settlement.dispute_reason_placeholder', 'Décrivez pourquoi vous contestez ce règlement...')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisputeDialogOpen(false)} disabled={actionLoading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleDisputeSettlement} 
            variant="contained" 
            color="error"
            disabled={actionLoading || !disputeReason.trim()}
          >
            {t('settlement.submit_dispute', 'Contester')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Escalate to Admin Dialog - when settlement deadline expired */}
      <Dialog open={escalateDialogOpen} onClose={() => setEscalateDialogOpen(false)}>
        <DialogTitle>
          ⚠️ {t('settlement.escalate_dialog_title', 'Escalader à un administrateur')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('settlement.escalate_dialog_warning', 
              'Le délai de règlement amiable est expiré. En escaladant ce litige, un administrateur prendra en charge la résolution.'
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settlement.escalate_dialog_info', 
              'L\'administrateur examinera les preuves et prendra une décision sur le litige. Les deux parties seront notifiées.'
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('settlement.escalate_reason_label', 'Raison (optionnel)')}
            value={escalateReason}
            onChange={(e) => setEscalateReason(e.target.value)}
            placeholder={t('settlement.escalate_reason_placeholder', 'Expliquez pourquoi vous souhaitez escalader...')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscalateDialogOpen(false)} disabled={actionLoading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleEscalateToAdmin} 
            variant="contained" 
            color="warning"
            disabled={actionLoading}
          >
            {t('settlement.escalate_confirm', 'Escalader')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Loading Overlay for async actions */}
      <LoadingOverlay
        isOpen={actionLoading}
        title={t('settlement.overlay.processing')}
        subtitle={t('settlement.overlay.pleaseWait')}
        warning={t('settlement.overlay.doNotClose')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
      />
    </>
  );
};

export default CashSettlementCard;
